import React from 'react';
import { StrengthData } from '../../../../MoleculeSearch.type';
import './StrengthCard.styles.scss';

export interface StrengthCardProps extends StrengthData {
  moleculeItem?: string;
  moleculeType?: string;
  moleculeLabel?: string;
}

const StrengthCard: React.FC<StrengthCardProps> = ({
  gfc = '',
  strength,
  dosageForms,
  marketOpportunity: _marketOpportunity,
  marketSize,
  allNdcs,
  ndcs,
  players,
  manufacturers = [],
  isClickable = false,
  moleculeItem = '',
  moleculeType = '',
  moleculeLabel = '',
}) => {
  const handleCardClick = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (gfc && gfc.trim() !== '') {
      // Build URL with all necessary parameters for breadcrumb
      const _encodedGfc = encodeURIComponent(gfc.trim());
      const params = new URLSearchParams();
      params.set('gfc', gfc.trim());

      // Add molecule search parameters if available
      if (moleculeItem) {
        params.set('item', moleculeItem);
      }
      if (moleculeType) {
        params.set('type', moleculeType);
      }
      if (moleculeLabel) {
        params.set('label', moleculeLabel);
      }

      // Add strength and dosage form for breadcrumb
      if (strength) {
        params.set('strength', strength);
      }
      if (dosageForms) {
        params.set('dosageForms', dosageForms);
      }

      const url = `/bdportal/product-wiki/product-analysis?${params.toString()}`;
      window.location.href = url;
    }
  };

  const _getOpportunityStyle = (opportunity: string) => {
    if (opportunity.includes('High') || opportunity.includes('Top')) {
      return { background: 'linear-gradient(135deg, var(--success-color), #059669)' };
    } else if (opportunity.includes('Medium')) {
      return { background: 'linear-gradient(135deg, var(--warning-color), #D97706)' };
    } else if (opportunity.includes('Competitive')) {
      return { background: 'linear-gradient(135deg, var(--error-color), #DC2626)' };
    } else {
      return { background: 'linear-gradient(135deg, var(--gray-500), var(--gray-600))' };
    }
  };

  return (
    <div
      className={`strength-card ${isClickable ? 'clickable' : ''}`}
      tabIndex={0}
      role="button"
    >
      <div className="strength-card-content">
        <div className="strength-header">
          <div>
            <div className="strength-value">{strength}</div>
            <div className="dosage-forms">{dosageForms}</div>
          </div>
          {/* <div className="market-opportunity" style={getOpportunityStyle(marketOpportunity)}>
            {marketOpportunity}
          </div> */}
        </div>
        <div className="strength-metrics">
          <div className="metric">
            <div className="metric-value">{marketSize}</div>
            <div className="metric-label">Market Size</div>
          </div>
          <div className="metric">
            <div className="metric-value">{allNdcs ?? 0}</div>
            <div className="metric-label">Total NDCs</div>
          </div>
          <div className="metric">
            <div className="metric-value">{ndcs}</div>
            <div className="metric-label">Active NDCs</div>
          </div>
          <div className="metric">
            <div className="metric-value">{players}</div>
            <div className="metric-label">Active Players</div>
          </div>
        </div>
        <div className="manufacturers-list">
          {manufacturers && manufacturers.length > 0
            ? manufacturers.map((manufacturer, index) => {
                const isLastItem = index === manufacturers.length - 1;
                const isOthersTag =
                  isLastItem && manufacturer.includes('+') && manufacturer.includes('others');

                if (isOthersTag) {
                  // Make "+x others" clickable to navigate to Competitors tab
                  return (
                    <span
                      key={index}
                      className="manufacturer-tag clickable-others"
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();

                        if (gfc && gfc.trim() !== '') {
                          // Build URL with all necessary parameters and set tab to competitors
                          const params = new URLSearchParams();
                          params.set('gfc', gfc.trim());
                          params.set('tab', 'competitors');

                          // Add molecule search parameters if available
                          if (moleculeItem) {
                            params.set('item', moleculeItem);
                          }
                          if (moleculeType) {
                            params.set('type', moleculeType);
                          }
                          if (moleculeLabel) {
                            params.set('label', moleculeLabel);
                          }

                          // Add strength and dosage form for breadcrumb
                          if (strength) {
                            params.set('strength', strength);
                          }
                          if (dosageForms) {
                            params.set('dosageForms', dosageForms);
                          }

                          const url = `/bdportal/product-wiki/product-analysis?${params.toString()}`;
                          window.location.href = url;
                        }
                      }}
                      title="Click to view all competitors"
                    >
                      {manufacturer}
                    </span>
                  );
                }

                return (
                  <span key={index} className="manufacturer-tag">
                    {manufacturer}
                  </span>
                );
              })
            : null}
        </div>
      </div>
      <div className="strength-actions">
        <button
          className="action-btn primary"
          onClick={e => handleCardClick(e)}
        >
          <span className="action-btn-text">
            View Analysis
          </span>
        </button>
      </div>
    </div>
  );
};

export default StrengthCard;
